import {
  ChevronDown,
  ChevronUp,
  DollarSign,
  Download,
  Eye,
  Filter,
  Folder,
  Info,
  Share2,
  Trash,
} from "lucide-react";
import { DEMO_IDS, getDemoAppFounderDashboard } from "@/mock/demo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Doc, Id } from "@cvx/_generated/dataModel";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ManualReviewDialog,
  MedianPerformanceCard,
  SimpleTotalVideosCard,
  TotalVideosCard,
} from "./Dashboard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency, formatNumber, truncateText } from "@/utils/format";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import CampaignAnalytics from "./CampaignAnalytics";
import { Checkbox } from "@/components/ui/checkbox";
import { ConvexError } from "convex/values";
import { Route as CreatorProfileRoute } from "@/routes/_app/_auth/creators/$creatorId/index";
import PlatformIcon from "../Generic/PlatformIcon";
import StatusBadge from "../Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { VideoProjectSidebar } from "./VideoProjectSidebar";
import { api } from "@cvx/_generated/api";
import { calculateEarningsForContract } from "@cvx/helpers";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";
import { useAccount } from "@/state/useAccount";
import { useAction } from "convex/react";
import { useConvexMutation } from "@convex-dev/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";

type Video = Doc<"contractVideos"> & {
  // Allow for demo data compatibility
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
  earnings?: number;
};

// Define the contract status type
type ContractStatus = "pendingApproval" | "active" | "completed" | "terminated";

// Define the video status type (for published videos/contractVideos)
// Note: needs_review is only for creatorVideoVersion, not contractVideos
type VideoStatus =
  | "ai_reviewing"
  | "ai_approved"
  | "ai_rejected"
  | "team_reviewing"
  | "team_rejected"
  | "brand_reviewing"
  | "approved"
  | "rejected";

// Define the video project status type
type VideoProjectStatus =
  | "draft"
  | "ai_reviewing"
  | "ai_approved"
  | "ai_rejected"
  | "team_reviewing"
  | "team_rejected"
  | "needs_review"
  | "brand_reviewing"
  | "approved"
  | "rejected";

// Define the video project type for sidebar
interface VideoProject {
  creatorName: string;
  projectTitle: string;
  status: VideoProjectStatus;
  contractStatus: string;
  projectId: Id<"creatorVideoProjects">;
  creatorId: Id<"creators">;
  contractId: Id<"creatorContracts">;
  createdAt?: number;
  linkedVideoTitle?: string;
}

export default function AppDashboard({
  campaignId,
}: {
  campaignId: Id<"campaigns">;
}) {
  const navigate = useNavigate();
  const { appId, demoModeEnabled, demoAppName } = useAccount();
  const { toast } = useToast();
  const currentCampaignId = campaignId as unknown as string;

  // Check if we're in demo mode (founder demo only)
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  // State for contract status filter
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  // State for video status filter (for challenge campaigns)
  const [selectedVideoStatuses, setSelectedVideoStatuses] = useState<string[]>(
    [],
  );
  // State for video project status filter (for video drafts tab)
  const [selectedVideoProjectStatuses, setSelectedVideoProjectStatuses] =
    useState<string[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [downloadingVideos, setDownloadingVideos] = useState<Set<string>>(
    new Set(),
  );
  const [deletingVideos, setDeletingVideos] = useState<Set<string>>(new Set());
  // State for current tab
  const [currentTab, setCurrentTab] = useState("posts");
  // State for sorting
  const [sortBy, setSortBy] = useState<"earnings" | null>("earnings");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  // State for video project sidebar
  const [selectedVideoProject, setSelectedVideoProject] =
    useState<VideoProject | null>(null);
  const [isVideoProjectSidebarOpen, setIsVideoProjectSidebarOpen] =
    useState(false);
  // State for share static dashboard dialog
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<string>>(
    () => new Set([currentCampaignId]),
  );

  // Get current user to check if they're a team member
  const { data: currentUser } = useQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
    enabled: !isDemo,
  });

  const {
    data: dashboardData,
    isLoading,
    refetch: refetchDashboard,
  } = useQuery({
    ...convexQuery(api.dashboard.getAppFoundersDashboard, {
      appId: appId as Id<"apps">,
      campaignId,
    }),
    enabled: !isDemo,
  });

  // Get all campaigns for the app to show in the share dialog
  const { data: allCampaigns } = useQuery({
    ...convexQuery(api.campaigns.getAllCampaigns, {
      appId: appId as Id<"apps">,
    }),
    enabled: !isDemo && isShareDialogOpen,
  });

  // Use demo data when in demo mode
  const effectiveDashboardData = isDemo
    ? getDemoAppFounderDashboard(demoAppName)
    : dashboardData;

  // Check if campaign has any review workflow enabled
  const campaign = effectiveDashboardData?.campaign;
  const hasReviewWorkflow = Boolean(
    campaign?.manualVideoReviewRequired ||
    campaign?.teamVideoReviewRequired ||
    campaign?.aiReviewRequired,
  );

  // Check if current user is a team member
  const isCreatorGridTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  const getDownloadUrlMutation = useAction(api.socials.getDownloadUrl);

  const { mutateAsync: deleteContractVideo } = useMutation({
    mutationFn: useConvexMutation(api.videos.deleteContractVideo),
  });

  // Get all unique contract statuses
  const allStatuses: ContractStatus[] = Array.from(
    new Set(
      effectiveDashboardData?.allCreators
        .map((creator) => creator.contract?.status)
        .filter((status): status is ContractStatus => status !== undefined) ||
      [],
    ),
  );

  // Check if Video Drafts tab should be shown
  const shouldShowVideoDraftsTab = Boolean(
    isCreatorGridTeamMember ||
    effectiveDashboardData?.videoProjectsDetails.some(
      (project) =>
        project.status.includes("brand") ||
        project.status === "approved" ||
        project.status === "rejected",
    ),
  );

  // Filter creators and videos based on selected contract statuses
  const filteredCreatorsAndContracts =
    effectiveDashboardData?.appCreatorContracts
      .filter((contract) => {
        // Filter by selected contract statuses
        if (!selectedStatuses.includes(contract.status)) {
          return false;
        }

        // If team video review is required and user is not a CreatorGrid team member and not in demo,
        // only include creators who have at least one video or video project in a brand-visible state
        const campaign = effectiveDashboardData?.campaign;
        if (
          campaign &&
          campaign.teamVideoReviewRequired &&
          !isCreatorGridTeamMember &&
          !isDemo
        ) {
          const hasBrandVisiblePublishedVideo =
            effectiveDashboardData?.publishedVideos?.some(
              (video) =>
                video.creatorId === contract.creatorId &&
                (video.status === "brand_reviewing" ||
                  video.status === "approved" ||
                  video.status === "rejected"),
            );

          const hasBrandVisibleVideoProject =
            effectiveDashboardData?.videoProjectsDetails?.some(
              (project) =>
                project.creatorId === contract.creatorId &&
                (project.status.includes("brand") ||
                  project.status === "approved" ||
                  project.status === "rejected"),
            );

          return hasBrandVisiblePublishedVideo || hasBrandVisibleVideoProject;
        }

        return true;
      })
      .map((contract) => {
        const creator = effectiveDashboardData?.allCreators.find(
          (c) => c._id === contract.creatorId,
        );
        if (!creator) return null;
        return {
          ...creator,
          contract,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null) || [];

  const filteredCreatorIds = new Set(
    filteredCreatorsAndContracts.map((creator) => creator._id),
  );

  // Get all unique video statuses from videos that the current user can actually see
  const allVideoStatuses: VideoStatus[] = Array.from(
    new Set(
      effectiveDashboardData?.publishedVideos
        .filter((video) => {
          // Apply the same visibility logic as in filteredVideos
          // Filter by contract status (creator must be in filtered list)
          if (!filteredCreatorIds.has(video.creatorId)) {
            return false;
          }

          // If teamVideoReviewRequired is true and user is not a team member,
          // only show brand-visible videos (brand_reviewing, approved, rejected)
          const campaign = effectiveDashboardData?.campaign;
          if (
            campaign &&
            campaign.teamVideoReviewRequired &&
            !isCreatorGridTeamMember &&
            !isDemo
          ) {
            return (
              video.status === "brand_reviewing" ||
              video.status === "approved" ||
              video.status === "rejected"
            );
          }

          return true;
        })
        .map((video) => video.status)
        .filter(
          (status): status is VideoStatus =>
            status !== undefined &&
            [
              "ai_reviewing",
              "ai_approved",
              "ai_rejected",
              "team_reviewing",
              "team_rejected",
              "brand_reviewing",
              "approved",
              "rejected",
            ].includes(status),
        ) || [],
    ),
  );

  // Get all unique video project statuses from all projects in the campaign
  // Note: This should NOT filter by contract status or visibility - those filters
  // are applied in filteredVideoProjects. This list is used to populate the filter dropdown.
  const allVideoProjectStatuses: VideoProjectStatus[] = Array.from(
    new Set(
      effectiveDashboardData?.videoProjectsDetails
        ?.map((project) => project.status)
        .filter(
          (status): status is VideoProjectStatus => status !== undefined,
        ) || [],
    ),
  );

  // Initialize selectedStatuses intelligently when data loads (only once)
  useEffect(() => {
    if (allStatuses.length > 0 && !hasInitialized) {
      // Check if our default "active" status exists in the data
      if (allStatuses.includes("active")) {
        // Keep the default ["active"] selection
        setSelectedStatuses(["active"]);
      } else if (allStatuses.length === 1) {
        // If there's only one status available, default to that
        setSelectedStatuses([allStatuses[0]]);
      } else {
        // If multiple statuses but no "active", select all to show something
        setSelectedStatuses([...allStatuses]);
      }
      setHasInitialized(true);
    }
  }, [allStatuses.length, hasInitialized]);

  useEffect(() => {
    setSelectedCampaignIds((prev) => {
      if (prev.has(currentCampaignId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(currentCampaignId);
      return next;
    });
  }, [currentCampaignId]);

  // Initialize video status filters intelligently for challenge campaigns
  useEffect(() => {
    if (
      effectiveDashboardData?.campaign?.type === "challenge" &&
      allVideoStatuses.length > 0
    ) {
      // Check if our default statuses exist in the data
      const defaultVideoStatuses = [
        "approved",
        "brand_reviewing",
        "team_reviewing",
        "ai_reviewing",
      ];
      const availableDefaults = defaultVideoStatuses.filter((status) =>
        allVideoStatuses.includes(status as VideoStatus),
      );

      if (availableDefaults.length > 0) {
        // Use the available default statuses
        setSelectedVideoStatuses(availableDefaults);
      } else if (allVideoStatuses.length === 1) {
        // If there's only one status available, default to that
        setSelectedVideoStatuses([allVideoStatuses[0]]);
      } else {
        // If multiple statuses but none match defaults, select all to show something
        setSelectedVideoStatuses([...allVideoStatuses]);
      }
    }
  }, [effectiveDashboardData?.campaign?.type, allVideoStatuses.length]);

  // Initialize video project status filters for Video Drafts tab
  useEffect(() => {
    if (shouldShowVideoDraftsTab && allVideoProjectStatuses.length > 0) {
      // Check if our default statuses exist in the data
      const defaultVideoProjectStatuses = [
        "ai_reviewing",
        "ai_approved",
        "team_reviewing",
        "brand_reviewing",
        "approved",
        "rejected",
      ];
      const availableDefaults = defaultVideoProjectStatuses.filter((status) =>
        allVideoProjectStatuses.includes(status as VideoProjectStatus),
      );

      if (availableDefaults.length > 0) {
        // Use the available default statuses (exclude draft by default)
        setSelectedVideoProjectStatuses(availableDefaults);
      } else if (allVideoProjectStatuses.length === 1) {
        // If there's only one status available, default to that
        setSelectedVideoProjectStatuses([allVideoProjectStatuses[0]]);
      } else {
        // If multiple statuses but none match defaults, select all non-draft statuses
        setSelectedVideoProjectStatuses(
          allVideoProjectStatuses.filter((status) => status !== "draft"),
        );
      }
    }
  }, [shouldShowVideoDraftsTab, allVideoProjectStatuses.length]);

  const filteredVideos =
    effectiveDashboardData?.publishedVideos.filter((video) => {
      // Filter by contract status (creator must be in filtered list)
      if (!filteredCreatorIds.has(video.creatorId)) {
        return false;
      }

      // For challenge campaigns, also filter by video status
      if (effectiveDashboardData?.campaign?.type === "challenge") {
        if (!selectedVideoStatuses.includes(video.status || "")) {
          return false;
        }
      }

      // If teamVideoReviewRequired is true and user is not a team member,
      // only show brand-visible videos (brand_reviewing, approved, rejected)
      const campaign = effectiveDashboardData?.campaign;
      if (
        campaign &&
        campaign.teamVideoReviewRequired &&
        !isCreatorGridTeamMember &&
        !isDemo
      ) {
        return (
          video.status === "brand_reviewing" ||
          video.status === "approved" ||
          video.status === "rejected"
        );
      }

      return true;
    }) || [];

  // Filter video projects based on selected contract statuses and video project statuses
  const filteredVideoProjects =
    effectiveDashboardData?.videoProjectsDetails?.filter((project) => {
      // Filter by contract status
      if (!selectedStatuses.includes(project.contractStatus)) {
        return false;
      }

      // Filter by video project status
      if (!selectedVideoProjectStatuses.includes(project.status)) {
        return false;
      }

      // If user is a CreatorGrid team member, show all video projects
      if (isCreatorGridTeamMember) {
        return true;
      }

      // If teamVideoReviewRequired is true and user is not a team member,
      // only show projects with brand-related statuses or approved/rejected
      const campaign = effectiveDashboardData?.campaign;
      if (campaign && campaign.teamVideoReviewRequired && !isDemo) {
        return (
          project.status.includes("brand") ||
          project.status === "approved" ||
          project.status === "rejected"
        );
      }

      return true;
    }) || [];

  // Create a map of published video IDs to their linked video projects
  const videoToProjectMap = useMemo(() => {
    const map = new Map<string, VideoProject>();
    if (effectiveDashboardData?.videoProjectsDetails) {
      for (const project of effectiveDashboardData.videoProjectsDetails) {
        // Check if this project has a contractVideoId (is linked to a published video)
        // TypeScript might not recognize contractVideoId if it's from demo data
        const contractVideoId =
          "contractVideoId" in project
            ? (project as { contractVideoId?: string | null }).contractVideoId
            : null;

        if (contractVideoId) {
          const videoProject: VideoProject = {
            creatorName: project.creatorName,
            projectTitle: project.projectTitle,
            status: project.status as VideoProjectStatus,
            contractStatus: project.contractStatus,
            projectId: project.projectId as Id<"creatorVideoProjects">,
            creatorId: project.creatorId as Id<"creators">,
            contractId: project.contractId as Id<"creatorContracts">,
            createdAt: project.createdAt,
            linkedVideoTitle:
              project.linkedVideoTitle !== null
                ? project.linkedVideoTitle
                : undefined,
          };
          map.set(contractVideoId, videoProject);
        }
      }
    }
    return map;
  }, [effectiveDashboardData?.videoProjectsDetails]);

  // Calculate filtered video status counts based on selected contract statuses
  const filteredVideoStatusCounts = {
    draft: 0,
    review: 0,
    approved: 0,
    needs_revision: 0,
  };

  if (effectiveDashboardData?.videoStatusCountsByContractStatus) {
    selectedStatuses.forEach((status) => {
      const counts =
        effectiveDashboardData.videoStatusCountsByContractStatus[status];
      if (counts) {
        filteredVideoStatusCounts.draft += counts.draft;
        filteredVideoStatusCounts.review += counts.review;
        filteredVideoStatusCounts.approved += counts.approved;
        filteredVideoStatusCounts.needs_revision +=
          "needs_revision" in counts
            ? counts.needs_revision
            : "rejected" in counts
              ? (counts as { rejected: number }).rejected
              : 0;
      }
    });
  }

  const handleStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedStatuses((prev) => [...prev, status]);
    } else {
      setSelectedStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleSelectAll = () => {
    setSelectedStatuses([...allStatuses]);
  };

  const handleSelectNone = () => {
    setSelectedStatuses([]);
  };

  const handleVideoStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedVideoStatuses((prev) => [...prev, status]);
    } else {
      setSelectedVideoStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleSelectAllVideoStatuses = () => {
    setSelectedVideoStatuses([...allVideoStatuses]);
  };

  const handleSelectNoneVideoStatuses = () => {
    setSelectedVideoStatuses([]);
  };

  const handleVideoProjectStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedVideoProjectStatuses((prev) => [...prev, status]);
    } else {
      setSelectedVideoProjectStatuses((prev) =>
        prev.filter((s) => s !== status),
      );
    }
  };

  const handleSelectAllVideoProjectStatuses = () => {
    setSelectedVideoProjectStatuses([...allVideoProjectStatuses]);
  };

  const handleSelectNoneVideoProjectStatuses = () => {
    setSelectedVideoProjectStatuses([]);
  };

  const handleCreatorClick = (creatorId: Id<"creators">) => {
    navigate({
      to: CreatorProfileRoute.fullPath,
      params: { creatorId },
    });
  };

  const handleVideoDownload = async (video: Video) => {
    try {
      setDownloadingVideos((prev) => new Set(prev).add(video._id));

      let downloadUrl = video.downloadUrl;

      // If no download URL exists, get it first
      if (!downloadUrl) {
        console.log("No download URL found, fetching...");

        // Show patience message
        toast({
          title: "Preparing download...",
          description:
            "Please be patient, this may take 10 seconds to 2 minutes to process the video download.",
          duration: 5000,
        });

        downloadUrl = await getDownloadUrlMutation({
          contractVideoId: video._id,
          url: video.url,
        });
      }

      if (downloadUrl) {
        // Show success message
        toast({
          title: "Download ready!",
          description: "The video download should start shortly.",
        });

        // Create a temporary anchor element to trigger download
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${video.title || "video"}.mp4`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to get download URL");
        toast({
          title: "Download failed",
          description: "Failed to get download URL. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error downloading video:", error);
      toast({
        title: "Download failed",
        description: "Failed to download video. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingVideos((prev) => {
        const newSet = new Set(prev);
        newSet.delete(video._id);
        return newSet;
      });
    }
  };

  const handleVideoDelete = async (video: Video) => {
    if (isDemo) {
      toast({
        title: "Demo Mode",
        description: "Video deletion is not available in demo mode",
        variant: "destructive",
      });
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete "${video.title || "this video"}"? This action cannot be undone and will remove all associated analytics data.`,
      )
    ) {
      return;
    }

    try {
      setDeletingVideos((prev) => new Set(prev).add(video._id));

      await deleteContractVideo({
        videoId: video._id,
      });

      toast({
        title: "Video deleted",
        description: "The video has been deleted successfully",
      });

      // Refetch dashboard data to update the list
      refetchDashboard();
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "Error deleting video",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error deleting the video",
        variant: "destructive",
      });
    } finally {
      setDeletingVideos((prev) => {
        const newSet = new Set(prev);
        newSet.delete(video._id);
        return newSet;
      });
    }
  };

  const handleUnpublishedVideosClick = (status?: string) => {
    navigate({
      to: "/dashboard/videosoverview",
      search: status ? { status } : undefined,
    });
  };

  const handleEarningsSort = () => {
    if (sortBy === "earnings") {
      // Toggle sort order if already sorting by earnings
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      // Set to sort by earnings, default to descending (highest first)
      setSortBy("earnings");
      setSortOrder("desc");
    }
  };

  const handleVideoProjectClick = (project: VideoProject) => {
    if (isDemo) {
      toast({
        title: "Demo Mode",
        description: "Video project editing is not available in demo mode",
        variant: "destructive",
      });
      return;
    }

    const videoProject: VideoProject = {
      creatorName: project.creatorName,
      projectTitle: project.projectTitle,
      status: project.status,
      contractStatus: project.contractStatus,
      projectId: project.projectId,
      creatorId: project.creatorId,
      contractId: project.contractId,
      createdAt: project.createdAt,
      linkedVideoTitle:
        project.linkedVideoTitle !== null
          ? project.linkedVideoTitle
          : undefined,
    };

    setSelectedVideoProject(videoProject);
    setIsVideoProjectSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsVideoProjectSidebarOpen(false);
    setSelectedVideoProject(null);
  };

  const handleRefetchDashboard = () => {
    // Refetch dashboard data to update video project statuses
    if (!isDemo) {
      refetchDashboard();
    }
  };

  const handleShareStaticDashboard = () => {
    if (selectedCampaignIds.size === 0) {
      toast({
        title: "No campaigns selected",
        description: "Please select at least one campaign to share.",
        variant: "destructive",
      });
      return;
    }

    const campaignIdsArray = [
      currentCampaignId,
      ...Array.from(selectedCampaignIds).filter(
        (id) => id !== currentCampaignId,
      ),
    ];
    navigate({
      to: "/static-dashboard",
      search: {
        campaignIds: campaignIdsArray.join(","),
      },
    });
  };

  const handleCampaignSelect = (campaignId: string, checked: boolean) => {
    setSelectedCampaignIds((prev) => {
      if (!checked && campaignId === currentCampaignId) {
        return prev;
      }
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(campaignId);
      } else {
        newSet.delete(campaignId);
      }
      newSet.add(currentCampaignId);
      return newSet;
    });
  };

  const handleSelectAllCampaigns = () => {
    if (!allCampaigns) return;
    const allCampaignIds = new Set<string>(
      allCampaigns.map((campaign) => campaign._id as unknown as string),
    );
    allCampaignIds.add(currentCampaignId);
    setSelectedCampaignIds(allCampaignIds);
  };

  const handleDeselectAllCampaigns = () => {
    setSelectedCampaignIds(new Set([currentCampaignId]));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCreatorSocials = (creator: any) => {
    if (!creator || !creator.socials)
      return <span className="text-xs text-primary/60">-</span>;
    const {
      instagram = [],
      tiktok = [],
      youtube = [],
    } = creator.socials as {
      instagram?: string[];
      tiktok?: string[];
      youtube?: string[];
    };

    const platforms: Array<{ key: string; handle: string; url: string }> = [];
    if (tiktok[0]) {
      const h = tiktok[0].replace(/^@/, "");
      platforms.push({
        key: "tiktok",
        handle: h,
        url: `https://www.tiktok.com/@${h}`,
      });
    }
    if (instagram[0]) {
      const h = instagram[0].replace(/^@/, "");
      platforms.push({
        key: "instagram",
        handle: h,
        url: `https://www.instagram.com/${h}`,
      });
    }
    if (youtube[0]) {
      const h = youtube[0].replace(/^@/, "");
      // Prefer @handle URL; if handle isn't actually an @handle, this still works for channels with handles
      platforms.push({
        key: "youtube",
        handle: h,
        url: `https://www.youtube.com/@${h}`,
      });
    }

    if (platforms.length === 0)
      return <span className="text-xs text-primary/60">-</span>;

    return (
      <div className="flex items-center gap-2">
        {platforms.slice(0, 3).map((p) => (
          <a
            key={p.key}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${p.key} @${p.handle}`}
            className="inline-flex h-5 w-5 items-center justify-center hover:opacity-80 transition-opacity"
          >
            <PlatformIcon platform={p.key} />
          </a>
        ))}
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-primary/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        {/* Stats Section */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div
            onClick={() => navigate({ to: "/dashboard/payouts" })}
            className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md"
          >
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Cost
                </h3>
                <DollarSign className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {formatCurrency(
                    effectiveDashboardData?.unpaidCreatorContractsToPay ?? 0,
                  )}
                </p>

                {/* Budget Progress Bar - only show if campaign has a prizePool */}
                {effectiveDashboardData?.campaign?.prizePool && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-primary/60">Budget Used</span>
                      <span className="text-primary/60">
                        {formatCurrency(
                          effectiveDashboardData.remainingBudget ?? 0,
                        )}{" "}
                        left
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${(effectiveDashboardData.totalCampaignEarnings ?? 0) /
                            effectiveDashboardData.campaign.prizePool >
                            0.8
                            ? "bg-red-500"
                            : (effectiveDashboardData.totalCampaignEarnings ??
                              0) /
                              effectiveDashboardData.campaign.prizePool >
                              0.6
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                        style={{
                          width: `${Math.min(
                            100,
                            ((effectiveDashboardData.totalCampaignEarnings ??
                              0) /
                              effectiveDashboardData.campaign.prizePool) *
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-primary/60">
                      <span>
                        {formatCurrency(
                          effectiveDashboardData.totalCampaignEarnings ?? 0,
                        )}{" "}
                        spent
                      </span>
                      <span>
                        {formatCurrency(
                          effectiveDashboardData.campaign.prizePool,
                        )}{" "}
                        budget
                      </span>
                    </div>
                  </div>
                )}

                <p className="text-xs text-primary/60">
                  Pending creator payments{" "}
                  {effectiveDashboardData?.campaign?.type === "challenge"
                    ? "(approved videos only)"
                    : ""}
                </p>
              </div>
            </div>
          </div>
          {effectiveDashboardData?.campaign?.type === "CiR" ||
            effectiveDashboardData?.campaign?.type === "challenge" ? (
            <SimpleTotalVideosCard publishedCount={filteredVideos.length} />
          ) : (
            <TotalVideosCard
              publishedCount={filteredVideos.length}
              videoStatusCounts={filteredVideoStatusCounts}
              onUnpublishedClick={handleUnpublishedVideosClick}
            />
          )}
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-primary/60">
                View Analytics
              </h3>
              <Eye className="h-5 w-5 text-primary/60" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary/60">Total Views</span>
                <span className="text-xl font-bold text-primary">
                  {formatNumber(
                    (filteredVideos as Video[]).reduce(
                      (sum: number, video: Video) => sum + (video.views ?? 0),
                      0,
                    ),
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary/60">
                  Median Views Per Video
                </span>
                <span className="text-xl font-bold text-primary">
                  {(() => {
                    const viewCounts = filteredVideos
                      .map((video) => video.views ?? 0)
                      .filter((views) => views > 0)
                      .sort((a, b) => a - b);

                    if (viewCounts.length === 0) return formatNumber(0);

                    const median =
                      viewCounts.length % 2 === 0
                        ? (viewCounts[Math.floor(viewCounts.length / 2) - 1] +
                          viewCounts[Math.floor(viewCounts.length / 2)]) /
                        2
                        : viewCounts[Math.floor(viewCounts.length / 2)];

                    return formatNumber(Math.round(median));
                  })()}
                </span>
              </div>
              {effectiveDashboardData?.campaign?.predefinedCpm !==
                undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-primary/60">
                      Cost Per 1K Views (CPM)
                    </span>
                    <span className="text-xl font-bold text-primary">
                      {formatCurrency(
                        effectiveDashboardData?.campaign?.predefinedCpm || 0,
                      )}
                    </span>
                  </div>
                )}
            </div>
          </div>
          <MedianPerformanceCard videos={filteredVideos as Video[]} />
        </div>

        <Tabs
          value={currentTab}
          onValueChange={setCurrentTab}
          className="w-full"
        >
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="posts">Published Posts</TabsTrigger>
              {shouldShowVideoDraftsTab && (
                <TabsTrigger value="video-drafts">Video Drafts</TabsTrigger>
              )}
              <TabsTrigger value="creators">Creators</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>

            {/* Only show filters on posts, creators, and video-drafts tabs */}
            {currentTab !== "analytics" && (
              <div className="flex items-center gap-2">
                {/* Share Static Dashboard Button */}
                {currentTab === "posts" && isCreatorGridTeamMember && (
                  <Dialog
                    open={isShareDialogOpen}
                    onOpenChange={setIsShareDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share static dashboard
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
                      <DialogHeader>
                        <DialogTitle>Share Static Dashboard</DialogTitle>
                        <DialogDescription>
                          Select campaigns to include in the shareable
                          dashboard. All approved videos from the selected
                          campaigns will be displayed.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 overflow-y-auto flex-1 px-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="selectAllCampaigns"
                              checked={
                                allCampaigns &&
                                selectedCampaignIds.size ===
                                allCampaigns.length &&
                                allCampaigns.length > 0
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  handleSelectAllCampaigns();
                                } else {
                                  handleDeselectAllCampaigns();
                                }
                              }}
                              disabled={
                                !allCampaigns || allCampaigns.length === 0
                              }
                            />
                            <label
                              htmlFor="selectAllCampaigns"
                              className="text-sm font-medium cursor-pointer"
                            >
                              Select All ({selectedCampaignIds.size}/
                              {allCampaigns?.length || 0})
                            </label>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDeselectAllCampaigns}
                            disabled={
                              !allCampaigns || allCampaigns.length === 0
                            }
                          >
                            Deselect All
                          </Button>
                        </div>
                        <TooltipProvider>
                          <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                            {!allCampaigns ? (
                              <div className="p-4 text-center text-sm text-primary/60">
                                Loading campaigns...
                              </div>
                            ) : allCampaigns.length === 0 ? (
                              <div className="p-4 text-center text-sm text-primary/60">
                                No campaigns available
                              </div>
                            ) : (
                              allCampaigns.map((campaign) => {
                                const campaignIdString =
                                  campaign._id as unknown as string;
                                const isCurrentCampaign =
                                  campaignIdString === currentCampaignId;

                                return (
                                  <div
                                    key={campaign._id}
                                    className="flex items-center space-x-3 p-3 hover:bg-secondary/50"
                                  >
                                    <Checkbox
                                      id={`campaign-${campaign._id}`}
                                      checked={
                                        isCurrentCampaign ||
                                        selectedCampaignIds.has(
                                          campaignIdString,
                                        )
                                      }
                                      onCheckedChange={(checked) =>
                                        handleCampaignSelect(
                                          campaignIdString,
                                          checked === true,
                                        )
                                      }
                                      disabled={isCurrentCampaign}
                                    />
                                    <label
                                      htmlFor={`campaign-${campaign._id}`}
                                      className="flex-1 cursor-pointer"
                                    >
                                      <div className="text-sm font-medium text-primary">
                                        {campaign.name || "Unnamed Campaign"}
                                      </div>
                                      <div className="text-xs text-primary/60">
                                        {campaign.type || "Unknown type"}
                                      </div>
                                    </label>
                                    {isCurrentCampaign && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-4 w-4 text-primary/40" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>
                                            Budget numbers will come from this
                                            campaign
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </TooltipProvider>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setIsShareDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleShareStaticDashboard}>
                          Share Dashboard
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {/* Contract Status Filter */}
                {allStatuses.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Contract ({selectedStatuses.length}/{allStatuses.length}
                        )
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>
                        Filter by Contract Status
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      <div className="flex items-center justify-between px-2 py-1">
                        <Button
                          onClick={handleSelectAll}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          All
                        </Button>
                        <Button
                          onClick={handleSelectNone}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          None
                        </Button>
                      </div>

                      <DropdownMenuSeparator />

                      {allStatuses.map((status) => (
                        <DropdownMenuCheckboxItem
                          key={status}
                          checked={selectedStatuses.includes(status)}
                          onCheckedChange={(checked) =>
                            handleStatusToggle(status, checked)
                          }
                        >
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={status} />
                            <span className="sr-only">
                              {status.replace("_", " ")}
                            </span>
                          </div>
                        </DropdownMenuCheckboxItem>
                      ))}

                      <DropdownMenuSeparator />

                      <div className="px-2 py-1 text-xs text-muted-foreground">
                        Showing {filteredCreatorsAndContracts.length} of{" "}
                        {effectiveDashboardData?.allCreators.length || 0}{" "}
                        creators
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Video Status Filter */}
                {allVideoStatuses.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Video ({selectedVideoStatuses.length}/
                        {allVideoStatuses.length})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>
                        Filter by Video Status
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      <div className="flex items-center justify-between px-2 py-1">
                        <Button
                          onClick={handleSelectAllVideoStatuses}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          All
                        </Button>
                        <Button
                          onClick={handleSelectNoneVideoStatuses}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          None
                        </Button>
                      </div>

                      <DropdownMenuSeparator />

                      {allVideoStatuses.map((status) => (
                        <DropdownMenuCheckboxItem
                          key={status}
                          checked={selectedVideoStatuses.includes(status)}
                          onCheckedChange={(checked) =>
                            handleVideoStatusToggle(status, checked)
                          }
                        >
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={status} />
                            <span className="sr-only">
                              {status.replace("_", " ")}
                            </span>
                          </div>
                        </DropdownMenuCheckboxItem>
                      ))}

                      <DropdownMenuSeparator />

                      <div className="px-2 py-1 text-xs text-muted-foreground">
                        Showing {filteredVideos.length} of{" "}
                        {effectiveDashboardData?.publishedVideos.length || 0}{" "}
                        videos
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Video Project Status Filter (for video drafts tab) */}
                {currentTab === "video-drafts" &&
                  allVideoProjectStatuses.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Filter className="h-4 w-4 mr-2" />
                          Status ({selectedVideoProjectStatuses.length}/
                          {allVideoProjectStatuses.length})
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuLabel>
                          Filter by Video Project Status
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <div className="flex items-center justify-between px-2 py-1">
                          <Button
                            onClick={handleSelectAllVideoProjectStatuses}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            All
                          </Button>
                          <Button
                            onClick={handleSelectNoneVideoProjectStatuses}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            None
                          </Button>
                        </div>

                        <DropdownMenuSeparator />

                        {allVideoProjectStatuses.map((status) => (
                          <DropdownMenuCheckboxItem
                            key={status}
                            checked={selectedVideoProjectStatuses.includes(
                              status,
                            )}
                            onCheckedChange={(checked) =>
                              handleVideoProjectStatusToggle(status, checked)
                            }
                          >
                            <div className="flex items-center space-x-2">
                              <StatusBadge status={status} />
                              <span className="sr-only">
                                {status.replace("_", " ")}
                              </span>
                            </div>
                          </DropdownMenuCheckboxItem>
                        ))}

                        <DropdownMenuSeparator />

                        <div className="px-2 py-1 text-xs text-muted-foreground">
                          Showing {filteredVideoProjects.length} of{" "}
                          {effectiveDashboardData?.videoProjectsDetails
                            ?.length || 0}{" "}
                          projects
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
              </div>
            )}
          </div>

          <TabsContent value="posts" className="mt-4">
            <div className="rounded-lg border border-border bg-card">
              <div className="overflow-x-auto">
                {!filteredVideos || filteredVideos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="mb-4 text-lg text-primary/60">
                      {selectedStatuses.length === 0
                        ? "No contract status selected"
                        : "No content has been posted yet"}
                    </p>
                    <p className="text-sm text-primary/60">
                      {selectedStatuses.length === 0
                        ? "Please select at least one contract status to view posts"
                        : "Content from your creators will appear here once they start posting"}
                    </p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="border-b border-border bg-secondary">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Username
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Creator
                        </th>
                        <th className="w-16 px-6 py-3 text-center text-sm font-medium text-primary/60">
                          Platform
                        </th>
                        {hasReviewWorkflow && (
                          <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                            Status
                          </th>
                        )}
                        <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                          Video Project
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Views
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Likes
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Bookmarks
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Shares
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Comments
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Payout
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Date Posted
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Last Updated
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                          Download
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(filteredVideos as Video[]).map((post: Video) => {
                        const creator = filteredCreatorsAndContracts.find(
                          (c) => c._id === post.creatorId,
                        );
                        const isDownloading = downloadingVideos.has(post._id);
                        const isDeleting = deletingVideos.has(post._id);
                        return (
                          <TooltipProvider key={post._id}>
                            <tr className="hover:bg-secondary/50">
                              <td className="px-6 py-4">
                                <a
                                  href={post.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary hover:underline"
                                >
                                  {truncateText(post.title ?? "No title")}
                                </a>
                              </td>
                              <td className="px-6 py-4">
                                {(() => {
                                  if (
                                    !creator ||
                                    !("socials" in creator) ||
                                    !creator.socials ||
                                    !post.platform
                                  ) {
                                    return (
                                      <span className="text-sm text-primary">
                                        -
                                      </span>
                                    );
                                  }
                                  const socials = creator.socials as {
                                    instagram?: string[];
                                    tiktok?: string[];
                                    youtube?: string[];
                                  };
                                  const platform = post.platform.toLowerCase();
                                  let handle = "";
                                  let url = "";

                                  if (
                                    platform === "tiktok" &&
                                    socials.tiktok?.[0]
                                  ) {
                                    handle = socials.tiktok[0].replace(
                                      /^@/,
                                      "",
                                    );
                                    url = `https://www.tiktok.com/@${handle}`;
                                  } else if (
                                    platform === "instagram" &&
                                    socials.instagram?.[0]
                                  ) {
                                    handle = socials.instagram[0].replace(
                                      /^@/,
                                      "",
                                    );
                                    url = `https://www.instagram.com/${handle}`;
                                  } else if (
                                    (platform === "youtube" ||
                                      platform === "youtube-shorts") &&
                                    socials.youtube?.[0]
                                  ) {
                                    handle = socials.youtube[0].replace(
                                      /^@/,
                                      "",
                                    );
                                    url = `https://www.youtube.com/@${handle}`;
                                  }

                                  if (handle && url) {
                                    return (
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline"
                                        title={`Open ${platform} profile @${handle}`}
                                      >
                                        @{handle}
                                      </a>
                                    );
                                  }

                                  return (
                                    <span className="text-sm text-primary">
                                      -
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() =>
                                    creator && handleCreatorClick(creator._id)
                                  }
                                  className="text-sm text-primary hover:underline"
                                >
                                  {creator?.name ?? "Unnamed Creator"}
                                </button>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-center">
                                  <PlatformIcon
                                    platform={post.platform ?? ""}
                                  />
                                </div>
                              </td>
                              {hasReviewWorkflow && (
                                <td className="px-6 py-4">
                                  <div className="flex items-center justify-center gap-2">
                                    {post.status === "brand_reviewing" ? (
                                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                        Pending your review
                                      </span>
                                    ) : (
                                      <StatusBadge
                                        status={post.status || "pending"}
                                        clickable={false}
                                        video={post}
                                        campaignType={
                                          effectiveDashboardData?.campaign?.type
                                        }
                                      />
                                    )}
                                    {(post.status === "ai_reviewing" ||
                                      post.status === "team_reviewing" ||
                                      post.status === "brand_reviewing" ||
                                      post.status === "approved") && (
                                        <ManualReviewDialog
                                          video={post}
                                          teamVideoReviewRequired={
                                            effectiveDashboardData?.campaign
                                              ?.teamVideoReviewRequired ?? false
                                          }
                                        />
                                      )}
                                  </div>
                                </td>
                              )}
                              <td className="px-6 py-4">
                                <div className="flex justify-center">
                                  {(() => {
                                    const linkedProject = videoToProjectMap.get(
                                      post._id,
                                    );
                                    if (linkedProject) {
                                      return (
                                        <Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleVideoProjectClick(
                                              linkedProject,
                                            );
                                          }}
                                          size="sm"
                                          variant="ghost"
                                          className="h-8 px-2 text-xs hover:bg-secondary"
                                          title={`View project: ${linkedProject.projectTitle}`}
                                        >
                                          <Folder className="h-4 w-4 mr-1" />
                                          {truncateText(
                                            linkedProject.projectTitle,
                                            20,
                                          )}
                                        </Button>
                                      );
                                    }
                                    return (
                                      <span className="text-xs text-primary/40">
                                        -
                                      </span>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatNumber(post.views ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatNumber(post.likes ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {post.platform === "youtube" ||
                                  post.platform === "youtube-shorts" ||
                                  post.platform === "tiktok"
                                  ? "N/A"
                                  : formatNumber(post.bookmarks ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {post.platform === "youtube" ||
                                  post.platform === "youtube-shorts"
                                  ? "N/A"
                                  : formatNumber(post.shares ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatNumber(post.comments ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm font-medium text-primary">
                                <span
                                  className={
                                    post.status === "rejected"
                                      ? "line-through opacity-60"
                                      : ""
                                  }
                                >
                                  {formatCurrency(post.earnings ?? 0)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {post.postDate
                                  ? format(new Date(post.postDate), "dd/MM/yy")
                                  : ""}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {post.lastUpdated
                                  ? format(
                                    new Date(post.lastUpdated),
                                    "dd/MM/yy",
                                  )
                                  : "-"}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-center">
                                  <Button
                                    onClick={() => handleVideoDownload(post)}
                                    disabled={isDownloading}
                                    size="sm"
                                    variant="outline"
                                    className="h-8 w-8 p-0"
                                    title={
                                      isDownloading
                                        ? "Processing download... This may take 10 seconds to 2 minutes"
                                        : "Download video"
                                    }
                                  >
                                    {isDownloading ? (
                                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                    ) : (
                                      <Download className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-center">
                                  <Button
                                    onClick={() => handleVideoDelete(post)}
                                    disabled={isDeleting || isDemo}
                                    size="sm"
                                    variant="destructive"
                                    className="h-8 w-8 p-0"
                                    title={
                                      isDeleting
                                        ? "Deleting..."
                                        : isDemo
                                          ? "Not available in demo mode"
                                          : "Delete video"
                                    }
                                  >
                                    {isDeleting ? (
                                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    ) : (
                                      <Trash className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          </TooltipProvider>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="video-drafts" className="mt-4">
            <div className="rounded-lg border border-border bg-card">
              <div className="overflow-x-auto">
                {!filteredVideoProjects ||
                  filteredVideoProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="mb-4 text-lg text-primary/60">
                      {selectedStatuses.length === 0
                        ? "No contract status selected"
                        : selectedVideoProjectStatuses.length === 0
                          ? "No video project status selected"
                          : "No video projects found"}
                    </p>
                    <p className="text-sm text-primary/60">
                      {selectedStatuses.length === 0
                        ? "Please select at least one contract status to view video projects"
                        : selectedVideoProjectStatuses.length === 0
                          ? "Please select at least one video project status to view projects"
                          : "Video projects from your creators will appear here"}
                    </p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="border-b border-border bg-secondary">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Project Title
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Creator
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Socials
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Linked Video
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredVideoProjects.map((project) => (
                        <tr
                          key={project.projectId}
                          className="cursor-pointer hover:bg-secondary/50"
                          onClick={() => {
                            const videoProject: VideoProject = {
                              creatorName: project.creatorName,
                              projectTitle: project.projectTitle,
                              status: project.status as VideoProjectStatus,
                              contractStatus: project.contractStatus,
                              projectId:
                                project.projectId as Id<"creatorVideoProjects">,
                              creatorId: project.creatorId as Id<"creators">,
                              contractId:
                                project.contractId as Id<"creatorContracts">,
                              createdAt: project.createdAt,
                              linkedVideoTitle:
                                project.linkedVideoTitle !== null
                                  ? project.linkedVideoTitle
                                  : undefined,
                            };
                            handleVideoProjectClick(videoProject);
                          }}
                        >
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-primary">
                              {project.projectTitle}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-primary">
                              {project.creatorName}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {renderCreatorSocials(
                              filteredCreatorsAndContracts.find(
                                (c) => c._id === project.creatorId,
                              ),
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              {project.status === "brand_reviewing" ? (
                                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                  Pending your review
                                </span>
                              ) : (
                                <StatusBadge status={project.status} />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-primary">
                              {project.linkedVideoTitle ? (
                                truncateText(project.linkedVideoTitle, 40)
                              ) : (
                                <span className="text-primary/60 italic">
                                  Not published
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-primary">
                            {format(new Date(project.createdAt), "dd/MM/yy")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="creators" className="mt-4">
            <div className="rounded-lg border border-border bg-card">
              <div className="overflow-x-auto">
                {!filteredCreatorsAndContracts ||
                  filteredCreatorsAndContracts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="mb-4 text-lg text-primary/60">
                      {selectedStatuses.length === 0
                        ? "No contract status selected"
                        : "No active creators yet"}
                    </p>
                    <p className="text-sm text-primary/60">
                      {selectedStatuses.length === 0
                        ? "Please select at least one contract status to view creators"
                        : "Partner with creators to start seeing their performance metrics here"}
                    </p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="border-b border-border bg-secondary">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Creator
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Contract Status
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                          Socials
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Total Views
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Total Posts
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          <button
                            onClick={handleEarningsSort}
                            className="flex items-center justify-end gap-1 hover:text-primary transition-colors"
                          >
                            Total Earnings
                            {sortOrder === "desc" ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronUp className="h-4 w-4" />
                            )}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(() => {
                        // Calculate earnings for each creator and prepare data for sorting
                        const creatorsWithEarnings =
                          filteredCreatorsAndContracts.map((creator) => {
                            const creatorVideos = (
                              filteredVideos as Video[]
                            ).filter(
                              (video: Video) => video.creatorId === creator._id,
                            );

                            // Filter out rejected videos for earnings calculations
                            const validCreatorVideos = creatorVideos.filter(
                              (video: Video) =>
                                video.status === "approved" || !video.status,
                            );

                            const totalViews = validCreatorVideos.reduce(
                              (sum: number, video: Video) =>
                                sum + (video.views ?? 0),
                              0,
                            );
                            const {
                              earningsFlatFee,
                              earningsPerVideoBonus,
                              earningsCPM,
                            } = calculateEarningsForContract(
                              creator.contract as Doc<"creatorContracts">,
                              validCreatorVideos as Doc<"contractVideos">[],
                            );

                            const totalEarnings =
                              earningsFlatFee +
                              earningsPerVideoBonus +
                              earningsCPM;

                            return {
                              creator,
                              creatorVideos,
                              validCreatorVideos,
                              totalViews,
                              totalEarnings,
                            };
                          });

                        // Sort creators if sorting is enabled
                        const sortedCreators =
                          sortBy === "earnings"
                            ? [...creatorsWithEarnings].sort((a, b) => {
                              return sortOrder === "desc"
                                ? b.totalEarnings - a.totalEarnings
                                : a.totalEarnings - b.totalEarnings;
                            })
                            : creatorsWithEarnings;

                        return sortedCreators.map(
                          ({
                            creator,
                            validCreatorVideos,
                            totalViews,
                            totalEarnings,
                          }) => (
                            <tr
                              key={creator._id}
                              className="cursor-pointer hover:bg-secondary/50"
                              onClick={() => handleCreatorClick(creator._id)}
                            >
                              <td className="px-6 py-4">
                                <span className="text-sm font-medium text-primary">
                                  {creator.name ?? "Unnamed Creator"}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {creator.contract?.status && (
                                  <StatusBadge
                                    status={creator.contract.status}
                                  />
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {renderCreatorSocials(creator)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatNumber(totalViews)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {validCreatorVideos.length}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatCurrency(totalEarnings)}
                              </td>
                            </tr>
                          ),
                        );
                      })()}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            <CampaignAnalytics campaignId={campaignId} />
          </TabsContent>
        </Tabs>

        {/* Video Project Sidebar */}
        <VideoProjectSidebar
          project={selectedVideoProject}
          isOpen={isVideoProjectSidebarOpen}
          onClose={handleCloseSidebar}
          onRefetch={handleRefetchDashboard}
          teamVideoReviewRequired={
            effectiveDashboardData?.campaign?.teamVideoReviewRequired ?? false
          }
        />
      </div>
    </div>
  );
}
